import { Controller } from "@hotwired/stimulus"
import {
  Chart,
  BarController,
  DoughnutController,
  BarElement,
  ArcElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip,
} from "chart.js"

Chart.register(
  BarController,
  DoughnutController,
  BarElement,
  ArcElement,
  LinearScale,
  CategoryScale,
  Legend,
  Tooltip
)

const SOURCE_COLORS = {
  web: "rgba(59, 130, 246, 0.85)",
  mobile: "rgba(16, 185, 129, 0.85)",
  mcp: "rgba(245, 158, 11, 0.85)",
  unknown: "rgba(107, 114, 128, 0.7)",
}

const ROOM_COLORS = ["rgba(99, 102, 241, 0.85)", "rgba(236, 72, 153, 0.85)"]

// Ruby wday: 0=Sun … 6=Sat. Display Mon→Sun.
const HEATMAP_DOWS = [1, 2, 3, 4, 5, 6, 0]

export default class extends Controller {
  static targets = ["dailyChart", "sourceChart", "roomTypeChart", "heatmap"]
  static values = {
    chartsDataUrl: String,
    labels: Object,
  }

  connect() {
    this.dailyInstance = null
    this.sourceInstance = null
    this.roomTypeInstance = null
    this.loadChartData()
  }

  disconnect() {
    this._destroyCharts()
  }

  async loadChartData() {
    try {
      const response = await fetch(this.chartsDataUrlValue, { credentials: "same-origin" })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      this.renderCharts(data)
    } catch (error) {
      console.error("[ChatStatsCharts] Error loading chart data:", error)
      this.renderCharts({
        labels: [],
        datasets: { by_source: {}, source_totals: [], source_labels: [], room_type_labels: [], room_type_totals: [] },
        heatmap: {},
      })
    }
  }

  renderCharts(data) {
    this._destroyCharts()
    const L = this.labelsValue || {}
    const labels = (data.labels || []).map((d) => this._shortDate(d))
    const bySource = data.datasets?.by_source || {}
    const sourceKeys = data.datasets?.source_labels || Object.keys(bySource)

    if (this.hasDailyChartTarget) {
      this.dailyInstance = new Chart(this.dailyChartTarget.getContext("2d"), {
        type: "bar",
        data: {
          labels,
          datasets: sourceKeys.map((key) => ({
            label: L[key] || key,
            data: bySource[key] || [],
            backgroundColor: SOURCE_COLORS[key] || SOURCE_COLORS.unknown,
            stack: "messages",
          })),
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "top" } },
          scales: {
            x: { stacked: true },
            y: { stacked: true, beginAtZero: true },
          },
        },
      })
    }

    if (this.hasSourceChartTarget) {
      const totals = data.datasets?.source_totals || []
      this.sourceInstance = new Chart(this.sourceChartTarget.getContext("2d"), {
        type: "doughnut",
        data: {
          labels: sourceKeys.map((k) => L[k] || k),
          datasets: [
            {
              data: totals,
              backgroundColor: sourceKeys.map((k) => SOURCE_COLORS[k] || SOURCE_COLORS.unknown),
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } },
        },
      })
    }

    if (this.hasRoomTypeChartTarget) {
      const roomLabels = (data.datasets?.room_type_labels || []).map((k) => L[k] || k)
      this.roomTypeInstance = new Chart(this.roomTypeChartTarget.getContext("2d"), {
        type: "doughnut",
        data: {
          labels: roomLabels,
          datasets: [
            {
              data: data.datasets?.room_type_totals || [],
              backgroundColor: ROOM_COLORS,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { position: "bottom" } },
        },
      })
    }

    if (this.hasHeatmapTarget) {
      this.renderHeatmap(data.heatmap || {}, L)
    }
  }

  renderHeatmap(grid, L) {
    const hours = [...Array(24).keys()]
    const weekdays = L.weekdays || ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    const eventsWord = L.events || "messages"
    const values = Object.values(grid).map(Number)
    const max = Math.max(0, ...values)

    const hourHeads = hours
      .map((h) => {
        const show = h % 3 === 0
        return `<th class="reports-heatmap__hour${show ? "" : " reports-heatmap__hour--muted"}">${show ? `${h}` : ""}</th>`
      })
      .join("")

    const rows = HEATMAP_DOWS.map((dow) => {
      const dayLabel = weekdays[dow] || String(dow)
      const cells = hours
        .map((h) => {
          const count = Number(grid[`${dow}-${h}`] || 0)
          const intensity = max > 0 ? count / max : 0
          const bg =
            count === 0
              ? "rgba(148, 163, 184, 0.12)"
              : `rgba(16, 185, 129, ${0.18 + intensity * 0.82})`
          const title = `${dayLabel} ${h}:00 — ${count} ${eventsWord}`
          return `<td class="reports-heatmap__cell" style="background:${bg}" title="${title}"></td>`
        })
        .join("")
      return `<tr><th class="reports-heatmap__day">${dayLabel}</th>${cells}</tr>`
    }).join("")

    this.heatmapTarget.innerHTML = `
      <div class="reports-heatmap">
        <table class="reports-heatmap__table" role="img" aria-label="heatmap">
          <thead><tr><th></th>${hourHeads}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="reports-heatmap__legend">
          <span>${L.legend_low || "Less"}</span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--0"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--1"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--2"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--3"></span>
          <span class="reports-heatmap__swatch reports-heatmap__swatch--4"></span>
          <span>${L.legend_high || "More"}</span>
        </div>
      </div>
    `
  }

  _shortDate(iso) {
    if (!iso) return ""
    const parts = String(iso).split("-")
    if (parts.length !== 3) return iso
    return `${parts[1]}/${parts[2]}`
  }

  _destroyCharts() {
    ;[this.dailyInstance, this.sourceInstance, this.roomTypeInstance].forEach((c) => {
      if (c) c.destroy()
    })
    this.dailyInstance = null
    this.sourceInstance = null
    this.roomTypeInstance = null
  }
}
